/**
 * The map, fetched after everything else.
 *
 * MapLibre is about a megabyte of the ~1.1 MB this app shipped, and importing
 * it from `main.js` put every byte of it in front of the first paint: the
 * browser could not so much as ask for the catalogue until the whole mapping
 * library had arrived and compiled. The catalogue, the lists, the search and
 * the venue card need none of it.
 *
 * So `map-view.js` is fetched on its own, and this stands in its place until
 * it lands. It answers the same five methods `createMapView` returns, and
 * remembers what it was told instead of discarding it, so the map catches up
 * the moment it exists.
 *
 * `update`, `setMe` and `setZoomControls` each describe a state rather than an
 * event, so the last call wins and is replayed. `resize` is dropped: a map
 * that has just been built has already measured itself, and one that does not
 * exist cannot be measured. `flyTo` is replayed too — the visit that gains
 * most from this whole arrangement is somebody opening a shared `/locale/…`
 * link, and that is exactly the visit where the map has to end up somewhere
 * specific. The cost of replaying it is small and short-lived: if the card was
 * closed while the library was in flight, the map arrives centred on the last
 * venue that was looked at.
 *
 * The missing-key case is answered here rather than in `map-view.js`, where it
 * used to live. A deployment without a MapTiler key now costs no MapLibre at
 * all instead of downloading it in order to report that it cannot be used.
 */

import { el } from './format.js';
import { isMapConfigured } from './map-key.js';

/** The methods that carry state worth replaying, in the order to replay it. */
const REPLAYED = ['update', 'setMe', 'setZoomControls', 'fitTo', 'flyTo'];

export function createDeferredMap(container, options) {
  if (!isMapConfigured()) {
    container.append(
      el(
        'p',
        'map__notice',
        'Mappa non disponibile al momento.'
      )
    );
    return {
      load: () => Promise.resolve(null),
      update() {},
      setMe() {},
      resize() {},
      fitTo() {},
      flyTo() {},
      setZoomControls() {},
    };
  }

  let real = null;
  let loading = null;
  /** The arguments of the last call to each replayed method. */
  const queued = Object.create(null);

  const remember =
    (name) =>
    (...args) => {
      if (real) real[name](...args);
      else queued[name] = args;
    };

  /**
   * Idempotent on purpose: it is called both when the map screen comes up and
   * from the idle callback after startup, and whichever happens first wins.
   */
  function load() {
    if (loading) return loading;
    loading = import('./map-view.js')
      .then(({ createMapView }) => {
        real = createMapView(container, options);
        for (const name of REPLAYED) {
          if (queued[name]) real[name](...queued[name]);
        }
        return real;
      })
      .catch((error) => {
        // The catalogue is readable without the map, so a chunk that fails to
        // arrive is reported and nothing else. Cleared so a later attempt —
        // the visitor coming back to the map tab — can try again.
        console.error('Mappa non caricata:', error);
        loading = null;
        return null;
      });
    return loading;
  }

  const view = { load, resize: () => real?.resize() };
  for (const name of REPLAYED) view[name] = remember(name);
  return view;
}
